/** @jsx jsx */

import React, {
  ReactElement,
  useRef,
  useEffect,
  cloneElement,
  useState,
} from 'react';
import { css, jsx } from '@emotion/core';
import isEDGE from 'services/isEDGE';
import { getHexToRgbWithDefaultFallback } from 'services/hexToRGB';
import { headingStylesForPanelsAndCards } from 'styles/headingStylesForPanelsAndCards';

interface FaceProps {
  id: string;
  face: string;
  color: string;
  includeFlipcardBackground?: boolean;
  cardBackgroundOpacity?: number;
  includeShadow?: boolean;
  textColor: string;
  alignTextTop: boolean;
  flipcardOutline?: boolean;
  flipcardOutlineColor?: number;
  flipcardBorderRadius?: number;
  zIndex: number;
  transform: string;
  visibleFace: string;
  visibility: boolean;
  children: ReactElement;
}

const Face = ({
  id,
  face,
  color,
  includeFlipcardBackground = true,
  cardBackgroundOpacity = 100,
  includeShadow = true,
  textColor,
  alignTextTop,
  flipcardOutline,
  flipcardOutlineColor,
  flipcardBorderRadius = 10,
  zIndex,
  transform = '',
  visibleFace,
  visibility,
  children,
  ...props
}: FaceProps) => {
  //

  const [mediaType, mediaTypeUpdated] = useState<number>(
    children?.props?.child ?? face === 'front' ? 1 : 0
  );

  const faceMedia = cloneElement(children, {
    onMediaChange(child) {
      mediaTypeUpdated(child);
    },
  });

  const { opacity } = props as any;
  const faceElement: any = useRef(null);
  const anchors: any = useRef(undefined);

  const { r, g, b } = getHexToRgbWithDefaultFallback(
    color,
    face === 'front' ? '#ffffff' : '#282A2B'
  );

  let a = cardBackgroundOpacity / 100;
  if (!color) {
    a = 0;
  }

  useEffect(() => {
    const stopPropagation = e => {
      e.stopPropagation();
    };

    const addRemoveStopPropagation = addRemove => {
      if (anchors.current && anchors.current.length) {
        anchors.current.forEach((a: any) => {
          a[`${addRemove}EventListener`]('click', stopPropagation);
        });
      }
    };

    if (!anchors.current) {
      anchors.current = Array.from(faceElement.current.querySelectorAll('a'));
      addRemoveStopPropagation('add');
    }

    return () => {
      addRemoveStopPropagation('remove');
    };
  }, []);

  useEffect(() => {
    if (anchors.current && anchors.current.length) {
      anchors.current.forEach((a: any) => {
        a.style.visibility = ['all', face].includes(visibleFace)
          ? 'visible'
          : 'hidden';
      });
    }
  }, [visibleFace]);

  let checkedTextColor = textColor;
  if (!textColor) {
    checkedTextColor = '#282a2b';
  }

  checkedTextColor = '';

  return (
    <div
      ref={faceElement}
      id={id}
      className="flipCardFace"
      css={css`
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 0px;
        top: 0px;
        width: 100%;
        height: 100%;

        ${mediaType === 0 &&
          `
          padding: ${window.dev() ? '10px' : '20px'};
          ${alignTextTop && 'align-items: flex-start;'}
        `}

        -webkit-backface-visibility: hidden;
        -moz-backface-visibility: hidden;
        backface-visibility: hidden;

        white-space: pre-wrap;
        overflow-wrap: break-word;

        // background-color: ${color};
        background: ${
          includeFlipcardBackground
            ? `rgba(${r}, ${g}, ${b}, ${a})`
            : 'transparent'
        };
        color: ${textColor};
        // color: #fff;
        z-index: ${zIndex};
        border: ${
          flipcardOutline ? `1px solid ${flipcardOutlineColor}` : 'none'
        };
        border-radius: ${flipcardBorderRadius}px;
        overflow: hidden;

        ${isEDGE() && `opacity: ${!opacity ? 1 : 0};`}

        ${transform && `transform: ${transform};`}
        transition: opacity 0.2s, transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);

        -webkit-transform-style: preserve-3d;
        transform-style: preserve-3d;

        ${includeShadow && 'box-shadow: 0 8px 24px 5px rgba(0, 0, 0, 0.2)'};

        * {
          line-height: 1.2em;
        }

        li {
          color: ${textColor};
        }

        li > span {
          color: ${textColor};
        }

        //double up needed for editing. . .
        * div {
          color: ${textColor};
        }

        ${headingStylesForPanelsAndCards}

       

      `}
      aria-hidden={!visibility}
    >
      {faceMedia}
    </div>
  );
};

export default Face;
